import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Card } from 'react-bootstrap';
import { readContract } from '@wagmi/core';
import * as configs from '../config.tsx'; // Configuration file
import communityRegistryABI from '../contracts/tgetherParameterGroupsRegistry.json'; // ABI for the Registry contract
import SetParamsModal from './FormModals/SetParamsModal.tsx'; // Import the modal component
import tgetherIncentiveABI from '../contracts/tgetherIncentives.json'; // ABI for the Incentive contract
import ProposeParamGroupModal from './FormModals/ProposeParamGroupModal.tsx';
interface ParameterGroupProps {
  groupName: string;
  community: string;
  isOwner: boolean; // Passed from the parent component
}

const ParameterGroupComponent: React.FC<ParameterGroupProps> = ({ groupName, community, isOwner }) => {
  const [params, setParams] = useState<any[]>([]); // Array to hold the fetched parameter values
  const [paramKeys, setParamKeys] = useState<string[]>([]); // The parameter names
  const [loading, setLoading] = useState(true);
  const [contractAddress, setContractAddress] = useState<`0x${string}`>('0x');
  const [decodedAbi, setDecodedAbi] = useState<any[]>([]); // ABI decoded from registry
  const [error, setError] = useState<string | null>(null);
  const [showSetParamsModal, setShowSetParamsModal] = useState(false); // Modal state
  const [showProposeParamsModal, setShowProposeParamsModal] = useState(false); // Modal state
  // Fetch contract address and encoded ABI using `parameterGroupsByName`
  const fetchGroupDetails = async () => {
    try {
      const groupDetails = await readContract(configs.config, {
        address: process.env.REACT_APP_PARAMETER_GROUP_REGISTRY_ADDRESS as `0x${string}`,
        abi: communityRegistryABI.abi,
        functionName: 'parameterGroupsByName',
        args: [groupName],
      }) as string[];
      if (groupDetails && groupDetails.length > 0) {
        const contractAddress = groupDetails[1]; // Contract address at index 1
        const encodedABI = groupDetails[2]; // Encoded ABI at index 2
        setContractAddress(contractAddress as `0x${string}`);
        const decodedABI = JSON.parse(atob(encodedABI)); // Decode the Base64 ABI and parse it to JSON
        setDecodedAbi(decodedABI as any[]);
      }
    } catch (err) {
      console.error('Error fetching parameter group details:', err);
      setError('Could not fetch parameter group details.');
    }
  };

  // Fetch parameters dynamically using `getParams` function from the ABI
  const fetchParamsAndKeys = async () => {
    if (!contractAddress || decodedAbi.length === 0) return;
    try {
      // Fetch parameter values from `getParams` function
      const paramDetails: any = await readContract(configs.config, {
        address: contractAddress,
        abi: decodedAbi,
        functionName: 'getParams',
        args: ["test"], // Assuming the community name is passed here
      });


      // Identify keys based on ABI inputs
      const abiFunction = decodedAbi.find((item) => item.name === 'getParams');
      const keys = abiFunction?.outputs.map((output: any) => output.name);

      // Set keys and params
      setParamKeys(keys);
      setParams(paramDetails);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching parameters or keys:', error);
      setError('Error fetching parameters or keys.');
      setLoading(false);
    }
  };

  // Fetch group details and parameters when the component loads
  useEffect(() => {
    fetchGroupDetails();
  }, [groupName]);

  useEffect(() => {
    if (contractAddress && decodedAbi.length > 0) {
      fetchParamsAndKeys();
    }
  }, [contractAddress, decodedAbi]);

  // Handle opening the SetParamsModal
  const handleSetParamsClick = () => {
    setShowSetParamsModal(true);
  };

  const handleProposeParamsClick = () => {
    setShowProposeParamsModal(true);
  };

  if (loading) return <div>Loading...</div>;

  if (error) return <div>{error}</div>;

  return (
    <div>
      <hr/>
      <h3 style={{ textAlign: 'center', color: '#93c', fontWeight:"1.3rem" }}>Parameter Group: {groupName}</h3>
      <p style={{ color: 'red', textAlign: 'center' }}>
        Warning: This is a community-created parameter group. Please ensure you understand its function before signing any transaction.
      </p>

      <Card style={{ margin: '20px', padding: '20px', borderRadius: '10px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
        <Card.Body>
          <h5 style={{ marginBottom: '20px', color: '#93c' }}>Parameter Values</h5>
          <Form>
            {paramKeys.length > 0 && params.length > 0 ? (
              paramKeys.map((key, idx) => (
                <Form.Group key={idx} className="mb-3">
                  <Form.Label style={{ fontWeight: 'bold', color: '#6c757d' }}>{key}</Form.Label>
                  <Form.Control
                    type="text"
                    value={params[idx].toString() || ''}
                    readOnly
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderColor: '#ced4da',
                      borderRadius: '5px',
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                </Form.Group>
              ))
            ) : (
              <Alert variant="danger">
                Parameter keys not found. Please check contract logic for key names.
              </Alert>
            )}
          </Form>
        </Card.Body>
      </Card>

      {isOwner && (
        <>
          <Button
            variant="primary"
            onClick={handleSetParamsClick}
            style={{ marginTop: '20px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          >
            Set Parameters
          </Button>
        </>
        
      )}

      {/* Render the modal for setting params */}
      {isOwner && (
        <SetParamsModal
          show={showSetParamsModal}
          onHide={() => setShowSetParamsModal(false)}
          abi={decodedAbi}
          contractAddress={contractAddress}
          groupName={groupName}
          communityName={community}
        />
      )}

      <Button
            variant="info"
            onClick={handleSetParamsClick}
            style={{ marginTop: '20px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          >
            Propose New Parameters
          </Button>
      <ProposeParamGroupModal
          show={showSetParamsModal}
          onHide={() => setShowProposeParamsModal(false)}
          abi={decodedAbi}
          contractAddress={contractAddress}
          groupName={groupName}
          communityName={community}
        />
    </div>
  );
};

export default ParameterGroupComponent;
